import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScrollView, YStack as CobuStack } from 'tamagui';

import { Strings } from '../../constants/Strings';
import { useManagePublicProfile } from '../../hooks/useManagePublicProfile';
import ImagePickerModal from '../../components/ui/ImagePickerModal';
import {
  CobuText,
  CobuSpacer,
  CobuHeader,
  CobuBackButton,
  CobuButton,
} from '@cobu/ui';
import {
  AboutSection,
  ProfileImageSection,
  SkillsSection,
} from '../../components/manage-public-profile';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import { showErrorMessage } from '../../utils/messageToast';

export default function ManagePublicProfileScreen() {
  const {
    // Data
    profileData,
    isProfileLoading,
    profileError,
    currentUser,
    currentImage,
    about,
    likesHelpingWith,
    needsHelpWith,
    showImagePicker,
    isLoading,

    // Handlers
    handleSave,
    handleImageSelected,
    handleShowImagePicker,
    handleHideImagePicker,
    handleLikesHelpingWithChange,
    handleNeedsHelpWithChange,
    setAbout,
  } = useManagePublicProfile();

  const strings = Strings.get();
  const navigate = useNavigate();

  useEffect(() => {
    /* ANALYTICS_DISABLED: OPEN_MANAGE_PUBLIC_PROFILE ('open_manage_public_profile')
      analyticsService.trackEvent(AnalyticsEvent.OPEN_MANAGE_PUBLIC_PROFILE);
      */
  }, []);

  // Error state
  const hasShownErrorRef = useRef(false);
  useEffect(() => {
    if (profileError && !hasShownErrorRef.current) {
      showErrorMessage(strings.common.somethingWentWrong);
      hasShownErrorRef.current = true;
    }
  }, [profileError]);

  // Loading state: if we don't have a current user yet, show a simple loading state
  if (!currentUser) {
    return (
      <CobuStack f={1} ai="center" jc="center" bg="$backgroundSecondary">
        <CobuText variant="body" color="$colorSecondary">
          {strings.profile.managePublicProfile.loading}
        </CobuText>
      </CobuStack>
    );
  }

  if (profileError) {
    return null;
  }

  const user = profileData?.user;
  if (!user) {
    return (
      <CobuStack f={1} ai="center" jc="center" bg="$backgroundSecondary">
        <CobuText variant="body" color="$colorSecondary">
          {strings.profile.managePublicProfile.noProfileDataFound}
        </CobuText>
      </CobuStack>
    );
  }

  return (
    <CobuStack f={1} bg="$backgroundSecondary" w="100%">
      <CobuHeader
        title={strings.profile.managePublicProfile.title}
        renderLeft={() => <CobuBackButton onPress={() => navigate(-1)} />}
        renderRight={() => (
          <CobuButton onPress={handleSave} disabled={isLoading}>
            <CobuText
              variant="body"
              color={isLoading ? '$backgroundMuted' : '$textLinkColor'}
              fontSize="$3"
              fow="600"
            >
              {strings.common.buttonLabels.save}
            </CobuText>
          </CobuButton>
        )}
      />
      <ScrollView
        contentContainerStyle={{ flexGrow: 1, width: '100%' }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        style={{ width: '100%' }}
      >
        <CobuStack gap="$4" pt="$4" f={1}>
          {/* Profile Image Section */}
          <ProfileImageSection
            currentImage={currentImage}
            user={user}
            onShowImagePicker={handleShowImagePicker}
          />

          {/* About Section */}
          <AboutSection
            about={about}
            onAboutChange={setAbout}
          />

          {/* Skills Section */}
          <SkillsSection
            likesHelpingWith={likesHelpingWith}
            needsHelpWith={needsHelpWith}
            onLikesHelpingWithChange={handleLikesHelpingWithChange}
            onNeedsHelpWithChange={handleNeedsHelpWithChange}
          />

          <CobuSpacer size="$4" />
        </CobuStack>
      </ScrollView>

      {/* Image Picker Modal */}
      {showImagePicker && (
        <ImagePickerModal
          onClose={handleHideImagePicker}
          onImageSelected={handleImageSelected}
        />
      )}
    </CobuStack>
  );
} 